import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronDown, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import DateFieldMx from "@/components/DateFieldMx";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DespachoInfoCallout,
  DespachoPageShell,
  DespachoSectionCard,
  DespachoSimpleTable,
  DespachoStatusBadge,
} from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import { currentMonthRange, currentYearStartIso } from "@/modules/despacho/despachoFiscalInfoHelpers";
import {
  ensureFiscalCurrentMonthSynced,
  resolveFiscalSyncPeriodKey,
} from "@/modules/despacho/despachoFiscalAutoSync";
import {
  TASK_ADVANCE_OPTIONS,
  groupTasksByClient,
  taskStatusLabel,
  taskStatusVariant,
} from "@/modules/despacho/operationalTaskHelpers";
import {
  DESPACHO_READ_ONLY_BADGE,
  isDespachoReadOnly,
} from "@/modules/despacho/despachoPermissions";
import {
  fiscalAssignmentBoardApi,
} from "@/modules/despacho/despachoFiscalAssignmentApi";
import {
  buildFiscalAuxiliarSummary,
  buildFiscalCompanySections,
} from "@/modules/despacho/despachoFiscalBoardHelpers";
import { mergeTasksReportByAuxiliarRows } from "@/modules/despacho/despachoWorkspaceAuxiliaries";
import { useDespachoWorkspaceAuxiliaries } from "@/modules/despacho/useDespachoWorkspaceAuxiliaries";
import TaskDetailDialog from "@/pages/despacho/TaskDetailDialog";
import { TareasGeneratePanel } from "@/pages/despacho/TareasGeneratePanel";
import TareasInternasPanel from "@/pages/despacho/TareasInternasPanel";
import TareasFiscalesPanel from "@/pages/despacho/TareasFiscalesPanel";
import {
  CalendarioGeneratePanel,
} from "@/pages/despacho/CalendarioCompliancePanels";
import {
  RemindersSummaryCards,
  RemindersGeneratePanel,
} from "@/pages/despacho/FiscalRemindersPanels";
import { useAuth } from "@/lib/auth";
import { formatIsoDateMx } from "@/lib/dateMx";
import { useCapabilities } from "@/lib/useCapabilities";
import { Badge } from "@/components/ui/badge";

const TAB_FISCALES = "fiscales";
const TAB_INTERNAS = "internas";
const TAB_GENERAR = "generar";

const TAB_FROM_QUERY = {
  fiscales: TAB_FISCALES,
  internas: TAB_INTERNAS,
  operativas: TAB_INTERNAS,
  // Cobranza vive en /despacho/honorarios (evita pestaña duplicada aquí).
  honorarios: TAB_FISCALES,
  // Recordatorios a clientes unificados en Calendario (bandeja).
  clientes: null,
  generar: TAB_GENERAR,
};

const PERIOD_ESTE_MES = "este_mes";
const PERIOD_TODAS = "todas";
const PERIOD_RANGO = "rango";

function formatDateLabel(iso) {
  return formatIsoDateMx(iso) === "—" ? "" : formatIsoDateMx(iso);
}

function buildPeriodLabel({ period, dateFrom, dateTo, monthRange }) {
  if (period === PERIOD_TODAS) return "Todas las fechas";
  if (period === PERIOD_ESTE_MES) {
    return `Este mes (${formatDateLabel(monthRange.from)} — ${formatDateLabel(monthRange.to)})`;
  }
  if (dateFrom && dateTo) {
    return `${formatDateLabel(dateFrom)} — ${formatDateLabel(dateTo)}`;
  }
  if (dateFrom) return `Desde ${formatDateLabel(dateFrom)}`;
  if (dateTo) return `Hasta ${formatDateLabel(dateTo)}`;
  return "Periodo personalizado";
}

function buildTaskColumns({ readOnly, advancingId, onAdvance, onDetail }) {
  return [
    {
      key: "due_date",
      label: "Fecha",
      render: (r) => (r.due_date ? String(r.due_date).slice(0, 10) : "—"),
    },
    { key: "client_name", label: "Cliente", render: (r) => r.client_name || "—" },
    { key: "task_name", label: "Tarea", render: (r) => r.task_name || r.title || "—" },
    {
      key: "obligation_period",
      label: "Obligación / periodo",
      render: (r) => {
        const obl = r.obligation_name || "";
        const per = r.period || "";
        if (obl && per) return `${obl} · ${per}`;
        return obl || per || "—";
      },
    },
    {
      key: "status",
      label: "Estado",
      render: (r) => (
        <DespachoStatusBadge variant={taskStatusVariant(r.status)}>
          {r.status_label || taskStatusLabel(r.status)}
        </DespachoStatusBadge>
      ),
    },
    {
      key: "_actions",
      label: "Acción",
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          <Button type="button" variant="ghost" size="sm" className="h-7" onClick={() => onDetail(r)}>
            Ver
          </Button>
          {!readOnly ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-7"
                  disabled={advancingId === r.id || ["done", "cancelled", "not_applicable_this_period", "completada"].includes(r.status)}
                  data-testid={`despacho-task-advance-${r.id}`}
                >
                  Avanzar
                  <ChevronDown className="h-3 w-3 ml-1" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {TASK_ADVANCE_OPTIONS.map((opt) => (
                  <DropdownMenuItem key={opt.value} onClick={() => onAdvance(r.id, opt.value)}>
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      ),
    },
  ];
}

export default function TareasDespacho() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions, role, isPlatformDesigner, loading: capabilitiesLoading } = useCapabilities();
  const readOnly = isDespachoReadOnly({ permissions, role, isPlatformDesigner, capabilitiesLoading, user });

  const qTabRaw = String(searchParams.get("tab") || "").toLowerCase();
  const initialTab = TAB_FROM_QUERY[qTabRaw] || TAB_FISCALES;
  const [tab, setTab] = useState(initialTab === null ? TAB_FISCALES : initialTab);
  const monthRange = useMemo(() => currentMonthRange(), []);
  const yearStart = useMemo(() => currentYearStartIso(), []);
  const [period, setPeriod] = useState(PERIOD_RANGO);
  const [dateFrom, setDateFrom] = useState(yearStart);
  const [dateTo, setDateTo] = useState(monthRange.to);
  const [tasks, setTasks] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadMessage, setLoadMessage] = useState("");
  const [advancingId, setAdvancingId] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reportAux, setReportAux] = useState([]);
  const [boardItems, setBoardItems] = useState([]);
  const [complianceInstances, setComplianceInstances] = useState([]);
  const [fiscalMetaState, setFiscalMetaState] = useState("idle");
  const [backgroundSyncing, setBackgroundSyncing] = useState(false);
  const fiscalLoadGenRef = useRef(0);
  const prevFiscalesTabRef = useRef(tab === TAB_FISCALES);
  const [fiscalPanelKey, setFiscalPanelKey] = useState(0);
  const { auxiliares, reload: reloadAuxiliares } = useDespachoWorkspaceAuxiliaries();

  useEffect(() => {
    const onFiscales = tab === TAB_FISCALES;
    if (onFiscales && !prevFiscalesTabRef.current) {
      setFiscalPanelKey((key) => key + 1);
    }
    prevFiscalesTabRef.current = onFiscales;
  }, [tab]);

  const periodLabel = useMemo(
    () => buildPeriodLabel({ period, dateFrom, dateTo, monthRange }),
    [period, dateFrom, dateTo, monthRange],
  );

  const listParams = useMemo(() => {
    const p = {};
    if (period === PERIOD_ESTE_MES) {
      p.date_from = monthRange.from;
      p.date_to = monthRange.to;
    } else if (period === PERIOD_RANGO) {
      if (dateFrom) p.date_from = dateFrom;
      if (dateTo) p.date_to = dateTo;
    }
    if (tab === TAB_FISCALES) p.generated_only = true;
    return p;
  }, [period, tab, monthRange.from, monthRange.to, dateFrom, dateTo]);

  const applyMonthFilter = () => {
    setPeriod(PERIOD_ESTE_MES);
    setDateFrom(monthRange.from);
    setDateTo(monthRange.to);
  };

  const applyAllFilter = () => {
    setPeriod(PERIOD_TODAS);
    setDateFrom("");
    setDateTo("");
  };

  const handleDateFromChange = (value) => {
    setDateFrom(value);
    if (value || dateTo) {
      setPeriod(PERIOD_RANGO);
    }
    if (value && dateTo && value > dateTo) setDateTo(value);
  };

  const handleDateToChange = (value) => {
    setDateTo(value);
    if (dateFrom || value) {
      setPeriod(PERIOD_RANGO);
    }
    if (value && dateFrom && value < dateFrom) setDateFrom(value);
  };

  const buildInstanceListParams = useCallback(() => {
    const instParams = {};
    if (period === PERIOD_ESTE_MES) {
      instParams.date_from = monthRange.from;
      instParams.date_to = monthRange.to;
    } else if (period === PERIOD_RANGO) {
      if (dateFrom) instParams.date_from = dateFrom;
      if (dateTo) instParams.date_to = dateTo;
    }
    return instParams;
  }, [period, monthRange.from, monthRange.to, dateFrom, dateTo]);

  const reloadFiscalData = useCallback(async ({ quiet = false } = {}) => {
    const instParams = buildInstanceListParams();
    if (!quiet) {
      setLoadState("loading");
      setFiscalMetaState("loading");
    }
    setLoadMessage("");
    try {
      const [rows, instances] = await Promise.all([
        despachoFiscalInfoApi.listTasks(listParams),
        despachoFiscalInfoApi.listComplianceInstances(instParams),
      ]);
      setTasks(rows);
      setComplianceInstances(instances);
      setFiscalMetaState("ready");
      setLoadState("ready");

      const [reportRes, boardRes] = await Promise.all([
        despachoFiscalInfoApi.listTasksReportByAuxiliar(),
        fiscalAssignmentBoardApi.listBoard(),
        reloadAuxiliares(),
      ]);
      setReportAux(reportRes);
      setBoardItems(Array.isArray(boardRes) ? boardRes : []);
      return true;
    } catch (err) {
      const parsed = parseFiscalInfoError(err);
      setTasks([]);
      setComplianceInstances([]);
      setBoardItems([]);
      setFiscalMetaState("ready");
      setLoadState(parsed.kind === "forbidden" ? "forbidden" : "error");
      setLoadMessage(parsed.message);
      return false;
    }
  }, [buildInstanceListParams, listParams, reloadAuxiliares]);

  const runBackgroundFiscalSync = useCallback(async (syncKey, loadGen) => {
    setBackgroundSyncing(true);
    try {
      const sync = await ensureFiscalCurrentMonthSynced({ monthKey: syncKey, notify: false });
      if (loadGen !== fiscalLoadGenRef.current) return;
      const inst = Number(sync?.instances_created || 0);
      const tasksN = Number(sync?.tasks_created || 0);
      await reloadFiscalData({ quiet: true });
      if (inst > 0 || tasksN > 0) {
        toast.message(`Se generaron ${inst} vencimiento(s) y ${tasksN} tarea(s) del mes.`);
      }
    } finally {
      if (loadGen === fiscalLoadGenRef.current) {
        setBackgroundSyncing(false);
      }
    }
  }, [reloadFiscalData]);

  const loadTasks = useCallback(async ({ skipBackgroundSync = false } = {}) => {
    if (tab === TAB_GENERAR || tab === TAB_INTERNAS) return;

    if (tab === TAB_FISCALES) {
      const syncKey = resolveFiscalSyncPeriodKey({ period, dateFrom, dateTo, monthRange });
      const loadGen = fiscalLoadGenRef.current + 1;
      fiscalLoadGenRef.current = loadGen;
      const ok = await reloadFiscalData({ quiet: false });
      if (ok && !skipBackgroundSync) {
        void runBackgroundFiscalSync(syncKey, loadGen);
      }
      return;
    }

    setLoadState("ready");
  }, [tab, monthRange, period, dateFrom, dateTo, reloadFiscalData, runBackgroundFiscalSync]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    const qTab = String(searchParams.get("tab") || "").toLowerCase();
    // Compat: antiguos enlaces Tareas → Clientes → bandeja unificada en Calendario.
    if (qTab === "clientes") {
      navigate("/despacho/calendario?tab=clientes&sub=bandeja", { replace: true });
      return;
    }
    const mapped = TAB_FROM_QUERY[qTab];
    if (mapped && mapped !== tab) setTab(mapped);
  }, [searchParams, tab, navigate]);

  const handleTabChange = (nextTab) => {
    setTab(nextTab);
    const next = new URLSearchParams(searchParams);
    if (nextTab === TAB_FISCALES) next.delete("tab");
    else next.set("tab", nextTab);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (tab !== TAB_GENERAR) return;
    let cancelled = false;
    (async () => {
      setFiscalMetaState("loading");
      await reloadAuxiliares();
      const [reportRes, boardRes] = await Promise.allSettled([
        despachoFiscalInfoApi.listTasksReportByAuxiliar(),
        fiscalAssignmentBoardApi.listBoard(),
      ]);
      if (cancelled) return;
      setReportAux(reportRes.status === "fulfilled" ? reportRes.value : []);
      setBoardItems(
        boardRes.status === "fulfilled" && Array.isArray(boardRes.value) ? boardRes.value : [],
      );
      setFiscalMetaState("ready");
    })();
    return () => { cancelled = true; };
  }, [tab, reloadAuxiliares]);

  const handleAdvance = async (taskId, nextStatus) => {
    if (readOnly || !taskId) return;
    setAdvancingId(taskId);
    try {
      const result = await despachoFiscalInfoApi.patchTask(taskId, { status: nextStatus });
      toast.success("Tarea actualizada.");
      if (result?.suggested_instance_status) {
        toast.message(`Sugerencia instancia: ${taskStatusLabel(result.suggested_instance_status)}`);
      }
      await loadTasks();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setAdvancingId("");
    }
  };

  const columns = useMemo(
    () => buildTaskColumns({
      readOnly,
      advancingId,
      onAdvance: handleAdvance,
      onDetail: (r) => {
        setDetailId(r.id);
        setDetailOpen(true);
      },
    }),
    [readOnly, advancingId],
  );

  const clientGroups = useMemo(() => groupTasksByClient(tasks), [tasks]);

  const mergedReportAux = useMemo(
    () => mergeTasksReportByAuxiliarRows(reportAux, auxiliares),
    [reportAux, auxiliares],
  );

  const auxSummary = useMemo(
    () => buildFiscalAuxiliarSummary({
      boardRows: boardItems,
      reportRows: mergedReportAux,
      tasks,
    }),
    [boardItems, mergedReportAux, tasks],
  );

  const companySections = useMemo(
    () => buildFiscalCompanySections(boardItems, tasks),
    [boardItems, tasks],
  );

  const fiscalPanelLoadState = loadState === "loading" || fiscalMetaState === "loading"
    ? "loading"
    : loadState;

  return (
    <DespachoPageShell
      title="Tareas"
      subtitle="Seguimiento operativo, generación de vencimientos y tareas por auxiliar."
      testId="despacho-tareas"
    >
      <Accordion type="single" collapsible className="rounded-sm border border-slate-200 bg-slate-50">
        <AccordionItem value="informacion-tareas" className="border-0">
          <AccordionTrigger
            className="px-4 py-3 text-xs text-slate-700 hover:no-underline [&[data-state=open]]:pb-2"
            data-testid="despacho-tareas-notice-trigger"
          >
            <span className="flex items-center gap-2 text-left">
              <Info className="w-4 h-4 shrink-0 text-slate-500" strokeWidth={1.75} aria-hidden />
              <span className="font-semibold text-[#0F2D5C]">Información de tareas</span>
            </span>
          </AccordionTrigger>
          <AccordionContent
            className="px-4 pb-3 pt-0 text-xs text-slate-600 leading-relaxed"
            data-testid="despacho-tareas-notice"
          >
            <p className="m-0 pl-6" role="note">
              El <strong>Calendario</strong> concentra plantillas, bandeja de mensajes al cliente y tablero de alertas.
              Use <strong>Internas</strong> para papelería y trámites especiales; <strong>Fiscales</strong> para
              vencimientos y seguimiento. <strong>Generar</strong> crea vencimientos, tareas y precarga de recordatorios
              (sin listas duplicadas: el tablero vive en{" "}
              <Link to="/despacho/calendario?tab=auto-alertas&sub=tablero" className="underline text-[#0F2D5C]">
                Calendario → Recordatorios programados
              </Link>
              ).
            </p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <RemindersSummaryCards className="mb-2" />

      {readOnly ? (
        <Badge variant="secondary" className="rounded-sm" data-testid="despacho-tareas-readonly">
          {DESPACHO_READ_ONLY_BADGE}
        </Badge>
      ) : null}

      <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full max-w-2xl grid-cols-3 h-auto mb-4" data-testid="despacho-tareas-tabs">
          <TabsTrigger value={TAB_FISCALES} className="text-xs rounded-sm">Fiscales</TabsTrigger>
          <TabsTrigger value={TAB_INTERNAS} className="text-xs rounded-sm" data-testid="despacho-tareas-tab-internas">
            Internas
          </TabsTrigger>
          <TabsTrigger value={TAB_GENERAR} className="text-xs rounded-sm" data-testid="despacho-tareas-tab-generar">
            Generar
          </TabsTrigger>
        </TabsList>

        {tab === TAB_FISCALES ? (
          <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="despacho-tareas-date-filter">
            <div>
              <Label htmlFor="tareas-desde" className="text-xs text-slate-600">Desde</Label>
              <DateFieldMx
                id="tareas-desde"
                value={dateFrom}
                onChange={handleDateFromChange}
                className="rounded-sm h-8 text-sm mt-0.5 w-40"
                minIso="2000-01-01"
                maxIso="2100-12-31"
                data-testid="despacho-tareas-date-from"
              />
            </div>
            <div>
              <Label htmlFor="tareas-hasta" className="text-xs text-slate-600">Hasta</Label>
              <DateFieldMx
                id="tareas-hasta"
                value={dateTo}
                onChange={handleDateToChange}
                className="rounded-sm h-8 text-sm mt-0.5 w-40"
                minIso="2000-01-01"
                maxIso="2100-12-31"
                data-testid="despacho-tareas-date-to"
              />
            </div>
            <div className="inline-flex rounded-sm border border-slate-200 bg-white p-0.5">
              <Button
                type="button"
                variant={period === PERIOD_ESTE_MES ? "default" : "ghost"}
                size="sm"
                className={`rounded-sm h-8 text-xs ${period === PERIOD_ESTE_MES ? "bg-[#0F2D5C] hover:bg-[#0a2248]" : ""}`}
                onClick={applyMonthFilter}
                data-testid="despacho-tareas-filter-este-mes"
              >
                Este mes
              </Button>
              <Button
                type="button"
                variant={period === PERIOD_TODAS ? "default" : "ghost"}
                size="sm"
                className={`rounded-sm h-8 text-xs ${period === PERIOD_TODAS ? "bg-[#0F2D5C] hover:bg-[#0a2248]" : ""}`}
                onClick={applyAllFilter}
                data-testid="despacho-tareas-filter-todas"
              >
                Todas
              </Button>
            </div>
            <p className="text-xs text-slate-500 m-0 pb-1 max-w-md">
              {periodLabel}
              {period === PERIOD_TODAS ? " · Elija Desde/Hasta para filtrar por rango." : ""}
            </p>
          </div>
        ) : null}

        <TabsContent value={TAB_FISCALES}>
          {tab === TAB_FISCALES ? (
            <TareasFiscalesPanel
              key={fiscalPanelKey}
              readOnly={readOnly}
              periodLabel={periodLabel}
              dateFrom={period === PERIOD_TODAS ? "" : dateFrom}
              dateTo={period === PERIOD_TODAS ? "" : dateTo}
              tasks={tasks}
              clientGroups={clientGroups}
              companySections={companySections}
              auxSummary={auxSummary}
              loadState={fiscalPanelLoadState}
              loadMessage={loadMessage}
              columns={columns}
              complianceInstances={complianceInstances}
              onInstancesChanged={loadTasks}
              backgroundSyncing={backgroundSyncing}
              boardItems={boardItems}
            />
          ) : null}
        </TabsContent>

        <TabsContent value={TAB_INTERNAS} className="mt-2">
          {tab === TAB_INTERNAS ? (
            <TareasInternasPanel readOnly={readOnly} onTasksChanged={loadTasks} />
          ) : null}
        </TabsContent>

        <TabsContent value={TAB_GENERAR}>
          <CalendarioGeneratePanel readOnly={readOnly} onGenerated={loadTasks} />
          <div className="mt-6">
            <TareasGeneratePanel readOnly={readOnly} onGenerated={loadTasks} />
          </div>
          <div className="mt-6">
            <RemindersGeneratePanel readOnly={readOnly} onGenerated={loadTasks} />
          </div>
          <div className="mt-4">
            <DespachoInfoCallout>
              Tablero de alertas precargadas:{" "}
              <Link to="/despacho/calendario?tab=auto-alertas&sub=tablero" className="underline text-[#0F2D5C]">
                Calendario → Recordatorios programados
              </Link>
              . Bandeja de mensajes al cliente:{" "}
              <Link to="/despacho/calendario?tab=clientes&sub=bandeja" className="underline text-[#0F2D5C]">
                Calendario → Bandeja de envío
              </Link>
              .
            </DespachoInfoCallout>
          </div>
          {reportAux.length ? (
            <DespachoSectionCard title="Pendientes por auxiliar" className="mt-4">
              <DespachoSimpleTable
                columns={[
                  { key: "auxiliar_name", label: "Auxiliar" },
                  { key: "total", label: "Total" },
                  { key: "pending", label: "Pendientes" },
                  { key: "in_progress", label: "En proceso" },
                  { key: "overdue", label: "Vencidas" },
                  { key: "done", label: "Terminadas" },
                  { key: "not_applicable", label: "No aplicó" },
                ]}
                rows={reportAux.map((r, i) => ({ ...r, id: r.assigned_to_user_id || `row-${i}` }))}
                emptyLabel="Sin datos."
              />
            </DespachoSectionCard>
          ) : null}
        </TabsContent>
      </Tabs>

      <TaskDetailDialog
        taskId={detailId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onUpdated={loadTasks}
        readOnly={readOnly}
      />
    </DespachoPageShell>
  );
}
